import React, { lazy, Suspense, useEffect } from 'react';

import './index.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/red-hat-display/400.css';
import '@fontsource/red-hat-display/500.css';
import '@fontsource/red-hat-display/600.css';
import '@fontsource/red-hat-display/700.css';

import AppClaimPanel from '@/features/claimPanel/App';
import TransmissionCreateLeadForm from '@/features/ecommerce/TransmissionCreateLeadForm';
import AppPit, { AppLoaderPit } from '@/features/pit/App';
import AppV2, { AppLoaderV2 } from '@/features/v2/App';
import { defaultLocale, langDynamic } from '@/lib/i18n';
import { queryClient } from '@/lib/query';
import RootBoundary from '@/routes/RootBoundary';
import UI from '@/routes/UI';

import AppCaseCard, { AppLoaderCaseCard } from './features/caseCard/App';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouteObject,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const AppOffer = lazy(() => import('@/features/offer/App'));

export const routes = [
  {
    path: '/:id/:step?',
    index: true,
    loader: AppLoaderV2(queryClient),
    element: <AppV2 />,
    errorElement: <RootBoundary />,
  },
  {
    path: '/case-card/:id?',
    index: true,
    loader: AppLoaderCaseCard(queryClient),
    element: <AppCaseCard />,
    errorElement: <RootBoundary caseCard />,
  },
  {
    path: '/claim/:id?',
    index: true,
    element: <AppClaimPanel />,
    errorElement: <RootBoundary caseCard />,
  },
  {
    path: '/offer/:hash',
    index: true,
    element: (
      <Suspense fallback={null}>
        <AppOffer />
      </Suspense>
    ),
    errorElement: <RootBoundary />,
  },
  // ECOMMERCE
  {
    path: '/ecommerce/transmission',
    index: true,
    element: <TransmissionCreateLeadForm />,
    errorElement: <RootBoundary />,
  },
  {
    path: '/ecommerce/transmission/:id/:step?',
    index: true,
    loader: AppLoaderV2(queryClient),
    element: <AppV2 />,
    errorElement: <RootBoundary />,
  },
  // PIT
  {
    path: '/pit/:id?',
    index: true,
    loader: AppLoaderPit(queryClient),
    element: <AppPit />,
    errorElement: <RootBoundary pit />,
  },
] as RouteObject[];

if (import.meta.env.VITE_SENTRY_LINK) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_LINK,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

if (import.meta.env.MODE !== 'production') {
  routes.push({
    path: '/ui/:id?',
    element: <UI />,
    errorElement: <RootBoundary />,
  });
}

const router = () => sentryCreateBrowserRouter(routes);

const EntryPoint = () => {
  useEffect(() => {
    const loadLang = async () => {
      await langDynamic(defaultLocale);
    };

    loadLang();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider i18n={i18n}>
        <meta name="theme-color" content="#16223E" />
        <RouterProvider router={router()} />
      </I18nProvider>
      <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
    </QueryClientProvider>
  );
};

async function enableMocking() {
  if (import.meta.env.VITE_MOCK !== 'true') {
    return;
  }

  const { worker } = await import('../mocks/browser');

  return worker.start();
}

enableMocking().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <>
      {import.meta.env.VITE_FE_DEBUG === 'true' ? (
        <React.StrictMode>
          <EntryPoint />
        </React.StrictMode>
      ) : (
        <EntryPoint />
      )}
    </>
  );
});
